/**
 * EL BORRADOR DEL REPORTE (12-10).
 *
 * A mitad del reporte mensual, "Atrás" del navegador (o recargar) sacaba de la pantalla y al
 * volver el formulario empezaba de cero. En vez de frenar la salida tocando el historial
 * del navegador (que ya rompió la navegación una vez, ver `components/ui/VisorDeFoto`), lo
 * escrito se guarda mientras se rellena y se recupera al volver: salir deja de costar nada.
 *
 * Se guarda en `sessionStorage`: vive lo que vive la pestaña y no sale del dispositivo. La
 * clave lleva de quién es, qué reporte y de qué ventana, así que el borrador de un reporte
 * no aparece en el siguiente. Se borra al enviar y al aplazar.
 *
 * Puro salvo el almacén, que se puede pasar (para probarlo). Si el almacén falla (modo
 * privado, cuota), no se guarda y no pasa nada: es una ayuda, no un requisito.
 *
 * EL PESO DE OTRO DÍA NO VUELVE (24-10). El reporte guarda su peso con el día en que se
 * manda: quien lo empieza el miércoles y lo acaba el jueves recuperaba en la casilla el
 * peso del miércoles, y se mandaba como el del jueves. El borrador lleva su día (el del
 * reloj del cliente, `hoyLocal`) y, si no es hoy, se recupera todo menos el peso.
 */
import { hoyLocal } from './horaEspana';

const PREFIJO = 'reporte-borrador:';

const almacenPorDefecto = () => {
    try { return window.sessionStorage; } catch { return null; }
};

/** La clave del borrador, o null si falta algo con lo que identificarlo. */
export function claveDelBorrador(clienteId, tipo, cierre) {
    if (!clienteId || !tipo || !cierre) return null;
    return `${PREFIJO}${clienteId}:${tipo}:${cierre}`;
}

/**
 * ¿Hay ya algo escrito que merezca guardarse? Del paso 2 en adelante (y en el resumen),
 * siempre. En el paso 1 solo si ha contestado alguna de sus preguntas (14-10, QUI-01):
 * cuando faltan check-ins el paso 1 PREGUNTA (las cinco valoraciones del quincenal y del
 * mensual), y salir de ahí las perdía todas porque el borrador no nacía hasta el paso 2.
 * Con el paso 1 sin tocar no se guarda nada: al volver no hay nada que "recuperar".
 */
export function hayAlgoQueGuardar(valores, paso, enResumen = false) {
    if (enResumen || paso >= 2) return true;
    const contestado = (v) => v != null && v !== '';
    // Las valoraciones del paso 1 son las `*_grado` (las claves las manda el servidor) y
    // los huecos van en `huecos_paso1`. El peso no cuenta: viene ya puesto.
    return Object.entries(valores || {}).some(([k, v]) => k.endsWith('_grado') && contestado(v))
        || Object.values(valores?.huecos_paso1 || {}).some(contestado);
}

export function guardarBorrador(clave, valores, paso, almacen = almacenPorDefecto(), hoy = hoyLocal()) {
    if (!clave || !almacen) return false;
    try {
        almacen.setItem(clave, JSON.stringify({ valores, paso, dia: hoy }));
        return true;
    } catch { return false; }
}

/** `{valores, paso}` o null. Un borrador que no se entiende se da por perdido. El de otro
 *  día vuelve sin el peso (ver arriba). */
export function leerBorrador(clave, almacen = almacenPorDefecto(), hoy = hoyLocal()) {
    if (!clave || !almacen) return null;
    try {
        const b = JSON.parse(almacen.getItem(clave) || 'null');
        if (!b || typeof b.valores !== 'object' || b.valores === null) return null;
        const valores = { ...b.valores };
        if (b.dia && b.dia !== hoy) delete valores.weight;
        return { valores, paso: Number.isInteger(b.paso) && b.paso > 0 ? b.paso : 1 };
    } catch { return null; }
}

export function borrarBorrador(clave, almacen = almacenPorDefecto()) {
    if (!clave || !almacen) return;
    try { almacen.removeItem(clave); } catch { /* nada que hacer */ }
}
